import { useExternalLogin } from '@common/auth/hooks/useExternalLogin';
import { clearSsoState } from '@common/auth/ssoAutoLogin/state';
import { Public_Routes } from '@common/url';
import { getProductTypeFromBasePath } from '@common/urlBasePath';
import { signin } from '@common/user-analytics';
import { LogoProphecyWithNameIcon } from '@prophecy/icons';
import { EventName, EventType, metrics } from '@prophecy/metrics';
import { resolveCssVariable, theme } from '@prophecy/theme';
import { Button, Display, Input, Password, Stack, Text } from '@prophecy/ui-v3';
import { toast } from '@prophecy/ui-v3/ui-compat';
import { capitalizeString } from '@prophecy/utils/string';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { useUnsecuredGlobalVariables } from '../../context/globalConfig/unsecuredConfigHook';
import { useMixPanel } from '../../context/mixpanel/context';
import { getUserPromptValue, getUserType, login } from '../../data/apis/api';
import { AuthTypes, ProviderType } from '../../data/apis/types';
import { errorHandler, useRestQuery } from '../../data/util';
import { useProphecyCredentialsManager } from '../../devtool/hooks';
import { DOMAIN_URL_KEY } from '../../utils/localstorage-keys';
import { IdpProviderLabelMap } from '../UserSettings/config/admin';
import {
  ExternalAuthButtons,
  Header,
  PageLink,
  StyledForm,
  StyledFormContainer,
  StyledLinkButton,
  StyledSeparator,
  useRedirectAfterAuth
} from './common';

const Form = styled(StyledForm)`
  flex-grow: 1;
`;
const StyledHeader = styled(Header)`
  position: initial;
  @media (max-width: ${resolveCssVariable(theme.breakpoints.s)}) {
    justify-content: center;
  }
`;
const ForgotPasswordLink = styled(StyledLinkButton)`
  align-self: flex-end;
` as typeof Button;
const StyledProphecyLogo = styled(LogoProphecyWithNameIcon)`
  && {
    width: 142px;
    height: 32px;
  }
`;

export default function SignInForm() {
  const { isLoading: isLoginLoading, mutateAsync: doLogin } = useMutation(login, {
    onError: errorHandler
  });
  const { isLoading: isUserInfoLoading, mutateAsync: getUserTypeInfo } = useMutation(getUserType, { retry: false });

  const { data } = useRestQuery(['UserPromptValue'], getUserPromptValue);

  const userPromptPlaceholder = data?.data?.promptValue || 'Enter your email';

  const [isAuthenticate, setAuthenticate] = useState(false);
  const { track } = useMixPanel();

  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email');
  const redirectParam = searchParams.get('redirect') || undefined;
  const [userType, setUserType] = useState<AuthTypes>();
  const [providerType, setProviderType] = useState<ProviderType>(ProviderType.okta);

  useEffect(() => {
    if (emailParam) {
      track(signin.pageLanding, { email: emailParam });
      metrics.track({
        eventName: EventName.ui.page.signin.signinLandingPage,
        eventType: EventType.userAction,
        payload: { email: emailParam }
      });
    }
  }, [emailParam, track]);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors }
  } = useForm({ mode: 'onChange', defaultValues: { email: emailParam || '', password: '' } });
  const unsecureGlobalConfigManager = useUnsecuredGlobalVariables();
  const { signInWithAzureAD, signInWithSaml, signInWithGoogle, idpTypesSupported } = useExternalLogin(
    false,
    redirectParam
  );
  const showCreateAccount = unsecureGlobalConfigManager.isSignupEnabled;
  const getAccessURL = unsecureGlobalConfigManager.getAccessURL;
  // ensure it is valid https redirect url
  const hasValidAccessURL = getAccessURL && getAccessURL.trim() !== '' && getAccessURL.startsWith('https://');
  const showForgotPassword = unsecureGlobalConfigManager.isSMTPEnabled;
  const type = userType?.toLowerCase();
  const { addCredential } = useProphecyCredentialsManager();

  const isSCIMEnabled = unsecureGlobalConfigManager.isSCIMEnabled;

  const isProphecyManagedUser = (authType: string) => {
    return authType === AuthTypes.prophecymanaged || authType.toLowerCase() === AuthTypes.ldap;
  };

  const redirectAfterAuth = useRedirectAfterAuth();

  const productType = getProductTypeFromBasePath();

  async function onSubmit({ email, password }: { email: string; password: string }) {
    track(signin.signinButtonClick, { email });
    metrics.track({
      eventName: EventName.ui.page.signin.login_attempt,
      eventType: EventType.userAction,
      payload: { email }
    });
    if (userType === AuthTypes.prophecymanaged || type === AuthTypes.ldap || isAuthenticate) {
      const xhrResp = await doLogin({
        identifier: email,
        password: password,
        loginType: userType as string
      });
      if (xhrResp.data.token) {
        // THIS IS VERY IMPORTANT
        // DO NOT REMOVE ANY OF THESE CONDITIONS
        // OTHERWISE CUSTOMER CREDENTIALS WILL BE STORED IN LOCAL STORAGE
        // AND YOU WILL BE FIRED :)
        if (process.env.NODE_ENV === 'development') {
          const isProphecyEmail = email.endsWith('@prophecy.io');
          const isLocalhost = window.location.hostname === 'localhost';
          const domainUrl = localStorage.getItem(DOMAIN_URL_KEY);

          if (isProphecyEmail && isLocalhost && domainUrl) {
            addCredential(email, password);
          }
        }

        // A password login contradicts the hint, so stop auto-redirecting this browser to the IdP.
        clearSsoState();
        redirectAfterAuth();
      }
    } else if (type === AuthTypes.saml) {
      await signInWithSaml(isSCIMEnabled ? { identifier: email } : {});
    } else if (type === AuthTypes.azuread) {
      await signInWithAzureAD({ identifier: email });
    } else if (type === AuthTypes.google) {
      await signInWithGoogle({ identifier: email, productType });
    } else {
      const { data } = await getUserTypeInfo({ identifier: email });
      if (data.success) {
        const userType = data.type as AuthTypes;
        const idp = data.idp as ProviderType;
        if (idp) {
          setProviderType(idp);
        }
        if (!isProphecyManagedUser(userType)) {
          setValue('password', userType);
        }
        setUserType(userType);
      } else {
        toast.error({ content: 'Invalid User credentials ' });
      }
    }
  }

  const getSignInButtonText = () => {
    let btnText = 'Continue';
    if (userType === AuthTypes.prophecymanaged || type === AuthTypes.ldap || isAuthenticate) {
      btnText = 'Sign In';
    } else if (type === AuthTypes.azuread) {
      btnText = 'Sign in with Azure';
    } else if (type === AuthTypes.saml) {
      const idpType = providerType || ProviderType.okta;
      let idpText = capitalizeString(IdpProviderLabelMap[idpType]);
      if (providerType === ProviderType.others) {
        idpText = AuthTypes.saml.toUpperCase();
      }
      btnText = `Sign in with ${idpText}`;
    } else if (type === AuthTypes.google) {
      btnText = 'Sign in with Google';
    }
    return btnText;
  };

  return (
    <Stack padding={`0 ${theme.spaces.x16}`} width='100%'>
      <StyledHeader>
        <StyledLinkButton
          elementType='anchor'
          variant='link'
          href={Public_Routes.Website.getUrl()}
          aria-label='Go to Prophecy homepage'
          data-test-id='prophecyLogo'>
          <StyledProphecyLogo aria-hidden='true' />
        </StyledLinkButton>
      </StyledHeader>
      <Form onSubmit={handleSubmit(onSubmit)} method='post' autoComplete='off'>
        <StyledFormContainer>
          <Stack gap={theme.spaces.x20} width='100%'>
            <Stack gap={theme.spaces.x32}>
              <Stack gap={theme.spaces.x12} alignY='center'>
                <Display level='sm' weight={theme.fontWeight.semiBold} tone={theme.colors.gray900}>
                  Log in
                </Display>
                <Text level='md' tone={theme.colors.gray500}>
                  Welcome back! Please enter your details.
                </Text>
              </Stack>
              <Stack gap={theme.spaces.x20}>
                <Controller
                  render={({ field }) => (
                    <Input
                      aria-label='Enter email address'
                      type='text'
                      placeholder={userPromptPlaceholder}
                      inputSize='l'
                      {...field}
                      variant={errors.email ? 'error' : 'primary'}
                      helpText={errors.email?.message}
                      data-test-id='signin-email-input'
                    />
                  )}
                  control={control}
                  name='email'
                  rules={{
                    required: 'Email is required'
                  }}
                />
                {((userType && isProphecyManagedUser(userType)) || isAuthenticate) && (
                  <Controller
                    render={({ field }) => (
                      <Password
                        aria-label='Enter password'
                        placeholder='******'
                        inputSize='l'
                        {...field}
                        variant={errors.password ? 'error' : 'primary'}
                        helpText={errors.password?.message}
                        data-test-id='signin-password-input'
                      />
                    )}
                    control={control}
                    name='password'
                    rules={{
                      required: 'Password is required'
                    }}
                  />
                )}
              </Stack>
            </Stack>
            {showForgotPassword && (
              <ForgotPasswordLink
                elementType='link'
                variant='link'
                to={Public_Routes.ResetPassword.getUrlWithBasePath(undefined, { redirect: redirectParam })}
                data-test-id='signin-forgot-password-link'>
                Forgot password
              </ForgotPasswordLink>
            )}
            <Button
              type='submit'
              size='l'
              loading={isLoginLoading || isUserInfoLoading}
              data-test-id='signin-submit-button'>
              {getSignInButtonText()}
            </Button>

            {showCreateAccount && idpTypesSupported.length > 0 && (
              <Stack width='100%' gap={theme.spaces.x16} align='center' alignY='center'>
                <Stack width='100%' align='center'>
                  <StyledSeparator />
                </Stack>
                <Text level='sm' tone={theme.colors.gray500}>
                  OR
                </Text>
              </Stack>
            )}
            {userType && !isProphecyManagedUser(userType) && !isAuthenticate && (
              <Button
                onClick={() => {
                  setAuthenticate(true);
                  setValue('password', '');
                }}
                variant='link'
                size='s'
                data-test-id='signin-login-with-username-password-button'>
                Login With Username and Password
              </Button>
            )}
            <ExternalAuthButtons isLogin={true} redirectUrl={redirectParam} />

            {showCreateAccount && (
              <Stack width='100%' gap={theme.spaces.x16} align='center' alignY='center'>
                <PageLink
                  label='Don’t have an account?'
                  buttonText='Sign up'
                  url={Public_Routes.SignUp.getUrlWithBasePath(undefined, { redirect: redirectParam })}
                  data-test-id='signin-signup-button'
                />
              </Stack>
            )}
            {hasValidAccessURL && (
              <Stack width='100%' gap={theme.spaces.x16} align='center' alignY='center'>
                <PageLink
                  label="Don't have access to Prophecy?"
                  buttonText='Get access'
                  href={getAccessURL}
                  target='_blank'
                  data-test-id='signin-get-access-button'
                />
              </Stack>
            )}
          </Stack>
        </StyledFormContainer>
      </Form>
    </Stack>
  );
}
